import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  KeyRound,
  Users,
  ShieldCheck,
  Truck,
  Activity,
  ArrowRight,
  Server,
  Database,
  Lock,
  Mail,
} from 'lucide-react';
import {
  PageHeader,
  Panel,
  PanelBody,
  SectionLabel,
  StatusPill,
} from '../ui/index.js';
import { fetchJson, me } from '../api.js';
import { BackupConsole } from '../components/BackupConsole.js';

interface PortalCredentialList {
  readonly masterKeySet: boolean;
  readonly portals: readonly unknown[];
}

interface BackupHealth {
  readonly status: 'ok' | 'stale' | 'not_configured' | 'empty';
  readonly count: number;
  readonly totalBytes: number;
}

export function SettingsPage(): React.ReactElement {
  useQuery({ queryKey: ['me'], queryFn: me });
  const credentialsQuery = useQuery({
    queryKey: ['portal-credentials'],
    queryFn: () => fetchJson<PortalCredentialList>('/api/v1/portals/credentials'),
  });

  const backupsHealthQuery = useQuery({
    queryKey: ['backups', 'health'],
    queryFn: () => fetchJson<BackupHealth>('/api/v1/backups/health'),
    refetchInterval: 15000,
  });

  const masterKeyOk = credentialsQuery.data?.masterKeySet ?? false;
  const backupOk = backupsHealthQuery.data?.status === 'ok';

  return (
    <div className="space-y-5">
      <PageHeader
        title="Général & Sauvegardes"
        hint="Sauvegardes automatisées Cloudflare R2 horaire/quotidienne, assistant de restauration à froid et diagnostic système."
        badge={
          <StatusPill tone={backupOk ? 'ok' : backupsHealthQuery.data?.status === 'not_configured' ? 'warn' : 'idle'}>
            {backupOk ? 'R2 Sauvegardes actives' : 'Sauvegardes R2'}
          </StatusPill>
        }
      />

      {/* Settings Navigation Subpages Cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <Link
          to="/settings/portals"
          className="group rounded-xl border border-border bg-card p-3.5 transition-all shadow-2xs hover:border-sky-300 hover:shadow-sm"
        >
          <div className="flex items-center justify-between">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-sky-50 text-sky-700 border border-sky-200">
              <KeyRound className="h-4 w-4" />
            </span>
            <ArrowRight className="h-4 w-4 text-ink-ghost transition-transform group-hover:translate-x-0.5 group-hover:text-sky-700" />
          </div>
          <div className="mt-2.5 text-xs font-bold text-ink group-hover:text-sky-800">Portails B2B</div>
          <div className="mt-0.5 text-[11px] text-ink-soft">Identifiants grossistes chiffrés & sondes</div>
        </Link>

        <Link
          to="/settings/users"
          className="group rounded-xl border border-border bg-card p-3.5 transition-all shadow-2xs hover:border-emerald-300 hover:shadow-sm"
        >
          <div className="flex items-center justify-between">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">
              <Users className="h-4 w-4" />
            </span>
            <ArrowRight className="h-4 w-4 text-ink-ghost transition-transform group-hover:translate-x-0.5 group-hover:text-emerald-700" />
          </div>
          <div className="mt-2.5 text-xs font-bold text-ink group-hover:text-emerald-800">Utilisateurs</div>
          <div className="mt-0.5 text-[11px] text-ink-soft">Comptes, mots de passe, PINs & sessions</div>
        </Link>

        <Link
          to="/settings/permissions"
          className="group rounded-xl border border-border bg-card p-3.5 transition-all shadow-2xs hover:border-purple-300 hover:shadow-sm"
        >
          <div className="flex items-center justify-between">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-purple-50 text-purple-700 border border-purple-200">
              <ShieldCheck className="h-4 w-4" />
            </span>
            <ArrowRight className="h-4 w-4 text-ink-ghost transition-transform group-hover:translate-x-0.5 group-hover:text-purple-700" />
          </div>
          <div className="mt-2.5 text-xs font-bold text-ink group-hover:text-purple-800">Permissions</div>
          <div className="mt-0.5 text-[11px] text-ink-soft">Matrice complète des droits RBAC</div>
        </Link>

        <Link
          to="/settings/logistics"
          className="group rounded-xl border border-border bg-card p-3.5 transition-all shadow-2xs hover:border-amber-300 hover:shadow-sm"
        >
          <div className="flex items-center justify-between">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-amber-50 text-amber-700 border border-amber-200">
              <Truck className="h-4 w-4" />
            </span>
            <ArrowRight className="h-4 w-4 text-ink-ghost transition-transform group-hover:translate-x-0.5 group-hover:text-amber-700" />
          </div>
          <div className="mt-2.5 text-xs font-bold text-ink group-hover:text-amber-800">Logistique</div>
          <div className="mt-0.5 text-[11px] text-ink-soft">Transporteurs, seuils retours & livreurs</div>
        </Link>

        <Link
          to="/settings/crawls"
          className="group rounded-xl border border-border bg-card p-3.5 transition-all shadow-2xs hover:border-herb-300 hover:shadow-sm"
        >
          <div className="flex items-center justify-between">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-herb-50 text-herb-700 border border-herb-200">
              <Activity className="h-4 w-4" />
            </span>
            <ArrowRight className="h-4 w-4 text-ink-ghost transition-transform group-hover:translate-x-0.5 group-hover:text-herb-700" />
          </div>
          <div className="mt-2.5 text-xs font-bold text-ink group-hover:text-herb-800">Crawls & crons</div>
          <div className="mt-0.5 text-[11px] text-ink-soft">Surveillance worker & planifications</div>
        </Link>

        <Link
          to="/settings/email"
          className="group rounded-xl border border-border bg-card p-3.5 transition-all shadow-2xs hover:border-rose-300 hover:shadow-sm"
        >
          <div className="flex items-center justify-between">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-rose-50 text-rose-700 border border-rose-200">
              <Mail className="h-4 w-4" />
            </span>
            <ArrowRight className="h-4 w-4 text-ink-ghost transition-transform group-hover:translate-x-0.5 group-hover:text-rose-700" />
          </div>
          <div className="mt-2.5 text-xs font-bold text-ink group-hover:text-rose-800">Emails transactionnels</div>
          <div className="mt-0.5 text-[11px] text-ink-soft">Mailgun / SES &amp; notifications</div>
        </Link>
      </div>

      {/* Main Feature: Cloudflare R2 Backup & Restore Console */}
      <Panel className="border-border">
        <PanelBody className="space-y-4">
          <SectionLabel>Console de Sauvegarde & Restauration (Cloudflare R2)</SectionLabel>
          <BackupConsole />
        </PanelBody>
      </Panel>

      {/* System Integrity & Diagnostics Card */}
      <Panel>
        <PanelBody className="space-y-3">
          <SectionLabel>Diagnostic Système & Chiffrement Maître</SectionLabel>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-border bg-slate-50/70 p-3.5 space-y-1">
              <div className="flex items-center gap-2 text-xs font-bold text-ink">
                <Lock className="h-4 w-4 text-herb-600" />
                <span>Clé Maîtresse (AES-256-GCM)</span>
              </div>
              <p className="text-[11px] text-ink-soft">
                {masterKeyOk ? (
                  <span className="text-emerald-700 font-semibold">
                    ✓ Actif (PORTAL_CREDENTIAL_KEY configurée)
                  </span>
                ) : (
                  <span className="text-rose-700 font-semibold">
                    ⚠️ Clé absente — Mots de passe portails bloqués
                  </span>
                )}
              </p>
            </div>

            <div className="rounded-xl border border-border bg-slate-50/70 p-3.5 space-y-1">
              <div className="flex items-center gap-2 text-xs font-bold text-ink">
                <Database className="h-4 w-4 text-sky-600" />
                <span>Base PostgreSQL & Prisma</span>
              </div>
              <p className="text-[11px] text-ink-soft">
                <span className="text-emerald-700 font-semibold">
                  ✓ Opérationnelle (pg_dump & WAL actifs)
                </span>
              </p>
            </div>

            <div className="rounded-xl border border-border bg-slate-50/70 p-3.5 space-y-1">
              <div className="flex items-center gap-2 text-xs font-bold text-ink">
                <Server className="h-4 w-4 text-purple-600" />
                <span>Environnement & Version</span>
              </div>
              <p className="text-[11px] text-ink-soft font-mono">
                v1.0.15 · Africa/Tunis · Fastify Node 22
              </p>
            </div>
          </div>
        </PanelBody>
      </Panel>
    </div>
  );
}
